import axios, { type AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from "axios";
import { ApiError, RELOGIN_REQUIRED_CODES } from "./errors";
import type { ApiFailure, ApiSuccess } from "./types";

/**
 * The ONE HTTP client (rules/01 §2), copied from the customer portal with one difference: the operator
 * cookie (`nacs_operator`) has no refresh, so a 401 from `/internal/*` ends the operator session at once
 * (the login page re-issues it from the person's still-valid customer session, or asks for a code).
 * Everything else is the same design: cookie session (withCredentials), CSRF header on every
 * state-changing request (the api origin may also hold a customer session, M5 §4), envelope unwrapping,
 * error-code mapping, and single-flight `/auth/refresh` for the customer-session calls (`/me`) —
 * never on a public path (no reload loops), never on a 5xx or a network blip.
 */
const API_PREFIX = "/api/v1";
const REQUEST_TIMEOUT_MS = 15_000;
const PUBLIC_PATHS = ["/login"];
/** A 401 from these is an answer, not an expired access token; refreshing would only loop. */
const AUTH_PATHS = [
  "/auth/operator/otp/request",
  "/auth/operator/otp/verify",
  "/auth/refresh",
  "/auth/logout",
  "/auth/logout-all",
  "/auth/dev-login",
  "/auth/operator-session",
];
const OPERATOR_PREFIX = "/internal/";

declare module "axios" {
  interface AxiosRequestConfig {
    /** Set on the retried copy of a request so a second 401 is terminal. */
    _retry?: boolean;
  }
}

export const http = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL ?? ""}${API_PREFIX}`,
  withCredentials: true,
  timeout: REQUEST_TIMEOUT_MS,
  headers: { Accept: "application/json" },
});

http.interceptors.request.use((config) => {
  const method = (config.method ?? "get").toUpperCase();
  if (method !== "GET" && method !== "HEAD" && method !== "OPTIONS") {
    config.headers.set("X-Requested-With", "nacs");
  }
  return config;
});

type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;
let unauthorizedInFlight = false;
let refreshInFlight: Promise<void> | null = null;

/** Registered once at bootstrap; a second registration replaces the first (tests). */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

/** Called after a successful sign-in so the next expired session redirects again. */
export function resetUnauthorizedGuard(): void {
  unauthorizedInFlight = false;
}

function isPublicPath(): boolean {
  if (typeof window === "undefined") return true;
  return PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p));
}

function pathOf(url: string | undefined): string {
  if (!url) return "";
  return url.startsWith(API_PREFIX) ? url.slice(API_PREFIX.length) : url;
}

function isAuthPath(url: string | undefined): boolean {
  const path = pathOf(url);
  return AUTH_PATHS.some((p) => path === p || path.startsWith(`${p}?`));
}

/** Operator routes: no refresh exists for the operator cookie, so an expired one is terminal. */
function isOperatorPath(url: string | undefined): boolean {
  return pathOf(url).startsWith(OPERATOR_PREFIX);
}

function failureFrom(error: AxiosError<ApiFailure>): ApiError {
  const body = error.response?.data;
  const status = error.response?.status ?? null;
  if (body && typeof body === "object" && body.success === false && body.error) {
    return new ApiError({
      code: body.error.code,
      message: body.error.message,
      status,
      details: body.error.details,
      requestId: body.meta?.requestId ?? null,
    });
  }
  if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
    return new ApiError({ code: "TIMEOUT", message: "Request timed out", status });
  }
  if (axios.isCancel(error)) return new ApiError({ code: "CANCELLED", message: "Request cancelled", status });
  if (!error.response) return new ApiError({ code: "NETWORK", message: "Network error", status: null });
  return new ApiError({ code: status === 401 ? "UNAUTHORIZED" : "UNKNOWN", message: error.message, status });
}

/** One refresh at a time; every 401 that arrives while it runs waits for the same result. */
function refreshSession(): Promise<void> {
  refreshInFlight ??= http
    .post("/auth/refresh")
    .then(() => undefined)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

function sessionLost(): void {
  if (isPublicPath() || unauthorizedInFlight) return;
  unauthorizedInFlight = true;
  unauthorizedHandler?.();
}

function isSessionExpiry(apiError: ApiError, config: InternalAxiosRequestConfig | undefined): boolean {
  return (
    apiError.status === 401 &&
    RELOGIN_REQUIRED_CODES.has(apiError.code) &&
    config !== undefined &&
    !config._retry &&
    !isAuthPath(config.url)
  );
}

http.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    const apiError = axios.isAxiosError<ApiFailure>(error)
      ? failureFrom(error)
      : new ApiError({ code: "UNKNOWN", message: error instanceof Error ? error.message : "Unknown error" });
    const config = axios.isAxiosError<ApiFailure>(error) ? error.config : undefined;
    if (!isSessionExpiry(apiError, config) || !config) throw apiError;

    if (isOperatorPath(config.url)) {
      sessionLost();
      throw apiError;
    }
    try {
      await refreshSession();
    } catch {
      sessionLost();
      throw apiError;
    }
    return http.request({ ...config, _retry: true });
  },
);

/** Full envelope, for callers that need `message`. */
export async function apiRequest<T>(config: AxiosRequestConfig): Promise<ApiSuccess<T>> {
  const response = await http.request<ApiSuccess<T>>(config);
  return response.data;
}

export async function apiGet<T>(
  url: string,
  params?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  const envelope = await apiRequest<T>({
    method: "GET",
    url,
    ...(params ? { params } : {}),
    ...(signal ? { signal } : {}),
  });
  return envelope.data;
}

export async function apiPost<T>(url: string, body?: unknown): Promise<T> {
  const envelope = await apiRequest<T>({ method: "POST", url, data: body });
  return envelope.data;
}

export async function apiPatch<T>(url: string, body?: unknown): Promise<T> {
  const envelope = await apiRequest<T>({ method: "PATCH", url, data: body });
  return envelope.data;
}

export async function apiPut<T>(url: string, body?: unknown): Promise<T> {
  const envelope = await apiRequest<T>({ method: "PUT", url, data: body });
  return envelope.data;
}

/** DELETE with an optional JSON body; no body means no Content-Type, which Fastify reads as null. */
export async function apiDelete<T>(url: string, body?: unknown): Promise<T> {
  const envelope = await apiRequest<T>({
    method: "DELETE",
    url,
    ...(body !== undefined ? { data: body } : {}),
  });
  return envelope.data;
}
